import {
    CommandBar,
    getTheme,
    ICommandBarItemProps,
    PanelType,
    PrimaryButton,
    SearchBox,
} from "@fluentui/react";
import React, {useEffect, useState} from "react";
import {
    useGetRecentWorkspaceRefsQuery,
    useGetWorkspaceSearchViewsQuery,
    useActivateWorkspaceMutation,
} from "../../store/Api";

import {
    useImportWorkspaceProductsFromTemplateMutation
} from "../../store/endpoints/WorkspaceItemsEndpoints";
import WorkspaceDeleteModal from "./WorkspaceDeleteModal";
import {setPunchoutWorkspace} from "../../store/settingsSlice";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import {useHistory} from "react-router-dom";

import PanelGridComposer from "../GridComposer/PanelGridComposer";
import {GridSelectionMode, IColumnDetails} from "../../models/GridConfiguration";
import {IWorkspace} from "../../models/Workspace";

interface IWorkspaceSearchModalProps {
    displayModal: boolean;
    closer: () => void;
}
const WorkspaceSearchModal: React.FC<IWorkspaceSearchModalProps> = props => {
    const isPunchoutWorkspace = window.location.pathname.toLowerCase().indexOf('punchout/workspace') !== -1;
    
    const getRecentWorkspaces = useGetRecentWorkspaceRefsQuery();
    const [selectedWorkspace, setSelectedWorkspace] = useState<string | undefined>();
    const [workspaceTypeFilter, setWorkspaceTypeFilter] = useState<string>("All")
    const [searchText, setSearchText] = useState<string>("")
    const [page, setPage] = useState<number>(1)
    const [pageSize, setPageSize] = useState<number>(25)
    const [filter, setFilter] = useState<string>()
    const [filterBy, setFilterBy] = useState<string>()
    const [sortBy, setSortBy] = useState<string>()
    const [sortDirection, setSortDirection] = useState<string>('desc')
    const workspaces = useGetWorkspaceSearchViewsQuery({filter: workspaceTypeFilter, searchText: searchText, conditions: {page: page, pageSize: pageSize, sortBy: sortBy, sortDirection: (sortBy? sortDirection : undefined), searchBy: filterBy, searchTerm: filter}});
    const [importProductsFromWorkspace] = useImportWorkspaceProductsFromTemplateMutation()
    const history = useHistory();
    const dispatch = useDispatch();
    
    const punchoutWorkspaceId = useSelector((state: RootState) => state.settings.punchoutWorkspace);
    const [deleteWorkspaceId, setDeleteWorkspaceId] = useState<string>()
    const [deleteWorkspaceModalOpen, setDeleteWorkspaceModalOpen] = React.useState(false);
    const [value] = useState<string | undefined>()
    const theme = getTheme();
    const [activateWorkspace] = useActivateWorkspaceMutation();
    const [isRefetching, setIsRefetching] = useState<boolean>(false);
    
    useEffect(() => {
        setPage(1)
    }, [searchText, workspaceTypeFilter]);

    useEffect(() => {
        // A user with no recent workspaces gets an empty list back, not nothing.
        if(getRecentWorkspaces.data?.length){
            setSelectedWorkspace(getRecentWorkspaces.data[0].id)
        }
    }, [getRecentWorkspaces.data]);
    
    const updateRefetch = async() => {
        setIsRefetching(true);
        await new Promise(f => setTimeout(f, 500));
        workspaces.refetch();
        setIsRefetching(false);
    }
    
    const commandItems: ICommandBarItemProps[] = [
        {
            key: 'allFilter',
            text: 'Active & Templates',
            onClick: () => setWorkspaceTypeFilter('All'),
            buttonStyles: {
                root: {
                    backgroundColor: (workspaceTypeFilter == 'All') ? '#ddd' : 'white'
                }
            },
        },
        {
            key: 'activeFilter',
            text: 'Active',
            onClick: () => setWorkspaceTypeFilter("Active"),
            buttonStyles: {
                root: {
                    backgroundColor: (workspaceTypeFilter == 'Active') ? '#ddd' : 'white'
                }
            },
        },
        {
            key: 'templatesFilter',
            text: 'Templates',
            onClick: () => setWorkspaceTypeFilter("Templates"),
            buttonStyles: {
                root: {
                    backgroundColor: (workspaceTypeFilter == 'Templates') ? '#ddd' : 'white'
                }
            },
        },
        {
            key: 'bundleFilter',
            text: 'Storefront Bundles',
            onClick: () => setWorkspaceTypeFilter("Bundles"),
            buttonStyles: {
                root: {
                    backgroundColor: (workspaceTypeFilter == 'Bundles') ? '#ddd' : 'white'
                }
            },
        },
        {
            key: 'inactiveFilter',
            text: 'Inactive',
            onClick: () => setWorkspaceTypeFilter("Inactive"),
            buttonStyles: {
                root: {
                    backgroundColor: (workspaceTypeFilter == 'Inactive') ? '#ddd' : 'white'
                }
            },
        }
        ]
    const rightCommands: ICommandBarItemProps[] = [
        {
            key: 'refresh',
            text: '',
            iconProps: { iconName: 'Refresh' },
            onClick: () => {workspaces.refetch(); workspaces.refetch(); }
        }
    ]
    
    const header = () => {
        return (
        <React.Fragment>
            <CommandBar items={commandItems} farItems={rightCommands}/>
            <br/>
            <SearchBox
                defaultValue={searchText ?? undefined}
                placeholder={"Search Workspace Name"}
                value={value}
                onChange={(e, d) => {setSearchText(d ?? '');
                }}
            />
        </React.Fragment>)
    }
    const buttonColumns: IColumnDetails[] = [
        {
            index: 0,
            identifier: 'Select',
            label: '',
            type: '',
            component:(ws: IWorkspace) => <PrimaryButton
                //disabled={selectedWorkspace == key.id} 
                onClick={() => {
                    setSelectedWorkspace(ws.id);

                    const newPath = isPunchoutWorkspace ? `/punchout/workspace/${ws.id}` : `/workspace/${ws.id}`;
                    history.replace(newPath);
                    props.closer()
                }
                }>Open</PrimaryButton>,
            required: true,
        },
        {
            identifier: 'Import',
            label: '',
            type: '',
            component:(a: IWorkspace) => selectedWorkspace && a.isTemplate && a.id != selectedWorkspace  && !isPunchoutWorkspace?
                <PrimaryButton iconProps={{iconName: 'WebTemplate'}} onClick={() => {
                    {
                        importProductsFromWorkspace({sourceId: a.id, destinationId: selectedWorkspace ?? ""})
                    }
                }}>Import</PrimaryButton> : <></>,
            required: true,
        },
        {
            identifier: 'Delete',
            label: '',
            type: '',
            component:(a: IWorkspace) => isPunchoutWorkspace || workspaceTypeFilter === "Inactive" ? <></> :
                <PrimaryButton styles={{root: { backgroundColor: theme.palette.red, color: theme.palette.white }, rootFocused: { backgroundColor: theme.palette.redDark, color: theme.palette.white }, rootHovered: { backgroundColor: theme.palette.redDark, color: theme.palette.white },}}
                               iconProps={{iconName: 'Delete'}}
                               onClick={async() => {{
                                   setDeleteWorkspaceId(a.id);
                                   setDeleteWorkspaceModalOpen(true);
                               }
                               }}>
                </PrimaryButton>,
            required: true,
        },
        {
            identifier: 'Activate',
            label: '',
            type: '',
            component:(a: IWorkspace) => workspaceTypeFilter != "Inactive" ? <></> :
                <PrimaryButton 
                   onClick={async() => {{
                       activateWorkspace(a.id);
                       await updateRefetch();
                   }
                   }}>Activate</PrimaryButton>,
            required: true,
        }
    ]
    return (
        <div>
            <PanelGridComposer
                getItemId={(item: IWorkspace) => item.id ?? ''}
                page={page}
                pageSize={pageSize}
                sortBy={sortBy}
                sortDirection={sortDirection}
                overrideColumns={buttonColumns}
                canChangePageSize={true}
                onDismiss={props.closer}
                isOpen={props.displayModal}
                panelTitle={'Search Workspaces'} 
                columnSetName={'WorkspaceSearchView'} 
                data={workspaces.data ?? {}} 
                loading={isRefetching || workspaces.isFetching || workspaces.isLoading} 
                onPageChange={setPage} 
                onPageSizeChange={setPageSize} 
                selectionMode={GridSelectionMode.none}
                onFilter={(filter, column) => {
                    setFilter(() => filter)
                    setFilterBy(() => column)
                }}
                onSort={(column, direction) => {
                    setSortBy(() => column)
                    setSortDirection(() => direction ?? 'desc')
                }}
                headerBottom={header()}
                type={PanelType.large}
            />
            <WorkspaceDeleteModal
                onDeleteWorkspace={() => {
                    if(deleteWorkspaceId && deleteWorkspaceId == punchoutWorkspaceId){
                        dispatch(setPunchoutWorkspace(undefined));
                    }
                    if(deleteWorkspaceId == selectedWorkspace){
                        // The page may hold only the deleted workspace, or nothing at all.
                        setSelectedWorkspace(workspaces.data?.items?.find(w => w.id != deleteWorkspaceId)?.id)
                    }
                }}
                selectedWorkspace={deleteWorkspaceId}
                displayModal={deleteWorkspaceModalOpen}
                closer={async() => {
                    setDeleteWorkspaceModalOpen(false);
                    await updateRefetch();
                }}
            />
        </div>

    );
}
export default WorkspaceSearchModal;